<template>
  <div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
    <!-- En-tête avec Tableau de bord et Extranet -->
    <div class="flex justify-between items-center py-8">
      <h1 class="text-4xl text-gray-700">
        Tableau de <span class="font-medium">bord</span>
      </h1>
      <div class="flex gap-4">
        <button
          class="inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-[#B70018] rounded-lg border-2 border-[#B70018] hover:bg-[#B70018] hover:text-white transition-colors duration-200"
          @click="refreshCache"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>Rafraîchir</span>
        </button>
        <a
          href="/extranet"
          class="inline-flex items-center justify-center gap-3 px-8 py-3 bg-white text-[#B70018] rounded-lg border-2 border-[#B70018] hover:bg-[#B70018] hover:text-white transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="{2}"
              d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
            />
          </svg>
          <span class="text-lg font-medium whitespace-nowrap">Extranet Fournisseurs</span>
        </a>
      </div>
    </div>

    <!-- Notification -->
    <Notification
      v-if="showNotification"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      :duration="5000"
    />

    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center py-8">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B70018]"
      />
    </div>

    <!-- Contenu principal -->
    <div v-if="!isLoading">
      <!-- Bandeau Semaine Promo stylisé -->
      <section v-if="promoStore.hasBanners" class="mb-8">
        <div
          v-for="banner in promoStore.banners"
          :key="banner.id"
          class="relative overflow-hidden shadow-lg rounded-2xl hover:shadow-xl transition-shadow duration-300 cursor-pointer"
          @click="router.push({ name: 'promo-home' })"
        >
          <div class="grid grid-cols-1 md:grid-cols-[350px_1fr] lg:grid-cols-[450px_1fr] h-auto md:h-[250px]">
            <!-- Logo à gauche sur fond blanc -->
            <div class="relative bg-white p-6 md:p-10 flex items-center justify-center z-20">
              <div class="relative w-full max-w-[280px] md:max-w-[380px] flex items-center justify-center">
                <img
                  v-if="banner.banner_logo_url"
                  :src="banner.banner_logo_url"
                  alt="Semaine Promo"
                  class="relative w-full h-auto object-contain"
                >
                <div v-else class="text-2xl md:text-3xl font-bold text-gray-800">
                  Semaine Promo
                </div>
              </div>
            </div>

            <!-- Contenu à droite avec fond rouge -->
            <div class="relative overflow-hidden min-h-[150px] md:min-h-0">
              <!-- Fond rouge -->
              <div class="absolute inset-0 z-0">
                <div class="absolute inset-0 bg-gradient-to-r from-[#8B0012] via-[#B70018] to-[#8B0012]" />
                <!-- Barre oblique - cachée sur mobile -->
                <div class="hidden md:block absolute -left-16 top-0 bottom-0 w-32 bg-white transform skew-x-[-20deg]" />
              </div>

              <!-- Contenu texte -->
              <div class="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-12 py-6 md:py-0">
                <h2 v-if="banner.banner_text" class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-wide text-white text-center">
                  {{ banner.banner_text }}
                </h2>

                <!-- Flèche avec animation -->
                <div class="absolute right-4 md:right-12 bg-white/20 hover:bg-white/30 rounded-full p-2 md:p-4 transition-all duration-300 cursor-pointer group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 md:h-10 md:w-10 text-white transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Bandeau Forum AGCO -->
      <section v-if="!forumBanner || forumBanner.visible" class="mb-8">
        <div
          class="relative overflow-hidden shadow-lg rounded-2xl hover:shadow-xl transition-shadow duration-300 cursor-pointer"
          @click="router.push({ name: 'forum-presentation' })"
        >
          <div class="grid grid-cols-1 md:grid-cols-[350px_1fr] lg:grid-cols-[450px_1fr] h-auto md:h-[250px]">
            <!-- Logo à gauche sur fond blanc -->
            <div class="relative bg-white p-6 md:p-10 flex items-center justify-center z-20">
              <img
                :src="forumBanner?.image_url || '/images/forum/agco-forum-logo.png'"
                alt="Forum AGCO"
                class="relative w-full max-w-[280px] md:max-w-[380px] h-auto object-contain"
              >
            </div>

            <!-- Contenu à droite : même charte que les autres bandeaux -->
            <div class="relative overflow-hidden min-h-[150px] md:min-h-0">
              <div class="absolute inset-0 z-0">
                <div class="absolute inset-0" :class="brandColors.gradient" />
                <div
                  class="hidden md:block absolute -left-16 top-0 bottom-0 w-32 bg-white transform"
                  :class="brandColors.skew"
                />
              </div>

              <div class="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-12 py-6 md:py-0">
                <h2
                  class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-wide text-center"
                  :class="brandColors.text"
                >
                  <strong class="font-bold">{{ forumBannerParts.bold }}</strong>
                  <template v-if="forumBannerParts.rest"> {{ forumBannerParts.rest }}</template>
                </h2>
                <div class="absolute right-4 md:right-12 bg-white/20 hover:bg-white/30 rounded-full p-2 md:p-4 transition-all duration-300 cursor-pointer group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 md:h-10 md:w-10 text-white transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Banderoles Admin AGCO -->
      <section v-if="canOrderCatalog" class="mb-8">
        <!-- Bon de commande -->
        <div
          class="relative overflow-hidden bg-white shadow-lg rounded-2xl hover:shadow-xl transition-shadow duration-300"
        >
          <div class="grid grid-cols-[300px_1fr] h-[200px]">
            <!-- Logo à gauche -->
            <div
              class="relative bg-white p-8 flex items-center justify-center z-20"
            >
              <div
                class="relative w-full max-w-[200px] flex flex-col items-center"
              >
                <img
                  :src="
                    currentBrand === 'agco'
                      ? '/images/logo-agco.svg'
                      : `/images/logo-${currentBrand}.svg`
                  "
                  :alt="brandAssets?.nameBrand"
                  class="relative w-full object-contain mb-4"
                >
              </div>
            </div>
            <!-- Barre oblique et contenu -->
            <div class="relative overflow-hidden">
              <!-- Forme en biais -->
              <div class="absolute inset-0 z-0">
                <div class="absolute inset-0" :class="brandColors.gradient" />
                <!-- Barre oblique -->
                <div
                  class="absolute -left-16 top-0 bottom-0 w-32 bg-white"
                  :class="brandColors.skew"
                />
              </div>
              <!-- Contenu -->
              <div
                class="relative z-10 h-full flex flex-col items-center justify-center px-12"
              >
                <h2
                  class="text-5xl font-light tracking-wide text-center"
                  :class="brandColors.text"
                >
                  Commandez votre <strong class="font-bold">catalogue</strong>
                </h2>
                <p
                  class="mt-2 text-lg text-center"
                  :class="brandColors.textLight"
                >
                  Découvrez nos nouveautés et personnalisez votre Catalogue
                </p>
                <!-- Flèche avec animation -->
                <div
                  class="absolute right-12 rounded-full p-4 transition-all duration-300 cursor-pointer group"
                  :class="brandColors.button"
                  @click="router.push('/order/catalog')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-8 w-8 text-white transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section Opération -->
      <section
        v-if="
          operation &&
            operation.catalog &&
            operation.operationGroup &&
            operation.operationGroup.slug === user?.brand
        "
        class="mb-8"
      >
        <div class="bg-white shadow rounded-lg p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">
            Opération en cours
          </h2>
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">
                {{ operation.name }}
              </h3>
              <p class="text-sm text-gray-500">
                {{ operation.description }}
              </p>
            </div>
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-500">Commande #{{ order?.reference }}</span>
              <button
                class="px-4 py-2 bg-[#B70018] text-white rounded-md hover:bg-[#B70018]/90"
              >
                Voir la commande
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Section Supports -->
      <section class="mb-8">
        <div class="bg-white shadow rounded-lg p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-6">
            Créez vos supports de <strong>communication</strong>
          </h2>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <!-- Affiche -->
            <div class="text-center h-full">
              <div
                class="bg-[#B70018]/5 rounded-lg p-6 cursor-pointer hover:bg-[#B70018]/10 hover:scale-105 transition-all duration-200 group flex flex-col h-full"
                @click="router.push('/supports/affiche/nouveau')"
              >
                <img
                  src="@/images/affiche.svg"
                  alt="Affiche"
                  class="h-16 w-auto mx-auto mb-4 group-hover:scale-110 transition-transform duration-200"
                >
                <div class="mt-auto flex flex-col justify-end h-14">
                  <h5 class="text-sm font-medium text-gray-900">
                    Affiche
                  </h5>
                  <small class="text-gray-500">(A4 - A3)</small>
                </div>
              </div>
            </div>

            <!-- Flyer -->
            <div class="text-center h-full">
              <div
                class="bg-[#B70018]/5 rounded-lg p-6 cursor-pointer hover:bg-[#B70018]/10 hover:scale-105 transition-all duration-200 group flex flex-col h-full"
                @click="router.push('/supports/flyer/nouveau')"
              >
                <img
                  src="@/images/flyer.svg"
                  alt="Flyer"
                  class="h-16 w-auto mx-auto mb-4 group-hover:scale-110 transition-transform duration-200"
                >
                <div class="mt-auto flex flex-col justify-end h-14">
                  <h5 class="text-sm font-medium text-gray-900">
                    Flyer A4
                  </h5>
                  <small class="text-gray-500">(Recto / Recto-Verso)</small>
                </div>
              </div>
            </div>

            <!-- Dépliant -->
            <div class="text-center h-full">
              <div
                class="bg-[#B70018]/5 rounded-lg p-6 cursor-pointer hover:bg-[#B70018]/10 hover:scale-105 transition-all duration-200 group flex flex-col h-full"
                @click="router.push('/supports/depliant/nouveau')"
              >
                <img
                  src="@/images/depliant.svg"
                  alt="Dépliant"
                  class="h-16 w-auto mx-auto mb-4 group-hover:scale-110 transition-transform duration-200"
                >
                <div class="mt-auto flex flex-col justify-end h-14">
                  <h5 class="text-sm font-medium text-gray-900">
                    Dépliant 4 pages
                  </h5>
                  <small class="text-gray-500">(A4 fermé)</small>
                </div>
              </div>
            </div>

            <!-- Banderole -->
            <div class="text-center h-full">
              <div
                class="bg-[#B70018]/5 rounded-lg p-6 cursor-pointer hover:bg-[#B70018]/10 hover:scale-105 transition-all duration-200 group flex flex-col h-full"
                @click="router.push('/supports/banderole/nouveau')"
              >
                <img
                  src="@/images/banderole.svg"
                  alt="Banderole"
                  class="h-16 w-auto mx-auto mb-4 group-hover:scale-110 transition-transform duration-200"
                >
                <div class="mt-auto flex flex-col justify-end h-14">
                  <h5 class="text-sm font-medium text-gray-900">
                    Banderole
                  </h5>
                </div>
              </div>
            </div>

            <!-- e-Mailing -->
            <div class="text-center h-full">
              <div
                class="bg-[#B70018]/5 rounded-lg p-6 cursor-pointer hover:bg-[#B70018]/10 hover:scale-105 transition-all duration-200 group flex flex-col h-full"
                @click="router.push('/supports/emailing/nouveau')"
              >
                <img
                  src="@/images/emailing.svg"
                  alt="e-Mailing"
                  class="h-16 w-auto mx-auto mb-4 group-hover:scale-110 transition-transform duration-200"
                >
                <div class="mt-auto flex flex-col justify-end h-14">
                  <h5 class="text-sm font-medium text-gray-900">
                    e-Mailing
                  </h5>
                </div>
              </div>
            </div>

            <!-- Visuel -->
            <div class="text-center h-full">
              <div
                class="bg-[#B70018]/5 rounded-lg p-6 cursor-pointer hover:bg-[#B70018]/10 hover:scale-105 transition-all duration-200 group flex flex-col h-full"
                @click="router.push('/supports/visuel-reseaux/nouveau')"
              >
                <img
                  src="@/images/visuel.svg"
                  alt="Visuel"
                  class="h-16 w-auto mx-auto mb-4 group-hover:scale-110 transition-transform duration-200"
                >
                <div class="mt-auto flex flex-col justify-end h-14">
                  <h5 class="text-sm font-medium text-gray-900">
                    Visuel
                  </h5>
                  <small class="text-gray-500">Réseaux sociaux</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section Documents -->
      <section class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <!-- Mes documents -->
        <div class="documents-grid">
          <div class="my-card shadow-lg rounded-lg">
            <div class="grid-card grid grid-cols-[200px_1fr] grid-rows-[250px]">
              <!-- Partie gauche -->
              <div class="bg-[#B70018] relative rounded-l-lg">
                <div class="p-3 relative z-10">
                  <h2 class="text-2xl font-medium text-white">
                    Mes <strong>documents</strong>
                  </h2>
                </div>
                <img
                  src="@/images/mes-documents.svg"
                  alt="Mes documents"
                  class="absolute bottom-0 left-0 right-0 w-full max-h-[65%] object-contain"
                >
              </div>
              <!-- Contenu à droite -->
              <div class="bg-white flex flex-col rounded-r-lg">
                <div class="last-documents p-6 flex-1">
                  <div class="grid grid-cols-3 gap-6">
                    <template v-if="recentDocuments.length > 0">
                      <div
                        v-for="doc in recentDocuments.slice(0, 3)"
                        :key="doc.id"
                        class="relative mx-auto overflow-hidden rounded-md border border-[#ebebeb] shadow-lg hover:scale-105 transition-transform duration-200 group w-full max-w-[130px] aspect-[210/297]"
                      >
                        <img
                          :src="thumbnailUrl(doc.thumb_name)"
                          :alt="doc.name"
                          class="w-full h-full object-cover"
                        >
                        <div
                          class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center"
                        >
                          <p
                            class="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-2"
                          >
                            {{ doc.name }}
                          </p>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        v-for="n in 3"
                        :key="n"
                        class="relative mx-auto overflow-hidden rounded-md border border-[#ebebeb] shadow-lg w-full max-w-[150px] aspect-[210/297] bg-gray-100 flex items-center justify-center"
                      >
                        <p class="text-gray-400 text-sm">
                          Aucun document
                        </p>
                      </div>
                    </template>
                  </div>
                </div>
                <div
                  class="h-[10%] bg-[#B70018] rounded-br-lg flex items-center justify-center"
                  style="margin-top: -35px"
                >
                  <button
                    class="flex items-center gap-2 text-white hover:text-white/90 transition-colors font-medium"
                  >
                    <a href="/documents"><span>Voir plus</span></a>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Documents AGCO -->
        <div class="documents-grid">
          <div class="my-card shadow-lg rounded-lg">
            <div class="grid-card grid grid-cols-[200px_1fr] grid-rows-[250px]">
              <!-- Partie gauche -->
              <div class="bg-[#B70018] relative rounded-l-lg">
                <div class="p-3 relative z-10">
                  <h2 class="text-2xl font-medium text-white">
                    Documents <strong>AGCO</strong>
                  </h2>
                </div>
                <img
                  src="@/images/documents-agco.svg"
                  alt="Documents AGCO"
                  class="absolute bottom-0 left-0 right-0 w-full max-h-[65%] object-contain"
                >
              </div>
              <!-- Contenu à droite -->
              <div class="bg-white flex flex-col rounded-r-lg">
                <div class="last-documents p-6 flex-1">
                  <div class="grid grid-cols-3 gap-6">
                    <template v-if="documentsAgco.length > 0">
                      <div
                        v-for="doc in documentsAgco.slice(0, 3)"
                        :key="doc.id"
                        class="relative mx-auto overflow-hidden rounded-md border border-[#ebebeb] shadow-lg hover:scale-105 transition-transform duration-200 group w-full max-w-[130px] aspect-[210/297]"
                      >
                        <img
                          :src="agcoThumbnailUrl(doc.thumb_name)"
                          :alt="doc.name"
                          class="w-full h-full object-cover"
                        >
                        <div
                          class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center"
                        >
                          <p
                            class="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-2"
                          >
                            {{ doc.name }}
                          </p>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        v-for="n in 3"
                        :key="n"
                        class="relative mx-auto overflow-hidden rounded-md border border-[#ebebeb] shadow-lg w-full max-w-[150px] aspect-[210/297] bg-gray-100 flex items-center justify-center"
                      >
                        <p class="text-gray-400 text-sm">
                          Aucun document
                        </p>
                      </div>
                    </template>
                  </div>
                </div>
                <div
                  class="h-[10%] bg-[#B70018] rounded-br-lg flex items-center justify-center"
                  style="margin-top: -35px"
                >
                  <button
                    class="flex items-center gap-2 text-white hover:text-white/90 transition-colors font-medium"
                  >
                    <a href="/documents-agco"><span>Voir plus</span></a>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section Catalogues -->
      <section class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <!-- Mes catalogues -->
        <div
          class="bg-white shadow rounded-lg p-4 min-h-0 max-h-[260px] flex flex-col justify-between"
        >
          <h2 class="text-base font-medium text-[#B70018] text-center mb-2">
            Mes catalogues
          </h2>
          <div class="flex space-x-2">
            <button
              :class="[
                'flex-1 px-2 py-1 rounded-md text-sm',
                selectedRefType === 'sans'
                  ? 'bg-[#B70018] text-white'
                  : 'border border-[#B70018] text-[#B70018] hover:bg-gray-50',
              ]"
              @click="selectedRefType = 'sans'"
            >
              Sans réf.
            </button>
            <button
              :class="[
                'flex-1 px-2 py-1 rounded-md text-sm',
                selectedRefType === 'avec'
                  ? 'bg-[#B70018] text-white'
                  : 'border border-[#B70018] text-[#B70018] hover:bg-gray-50',
              ]"
              @click="selectedRefType = 'avec'"
            >
              Avec réf.
            </button>
          </div>
          <!-- Sans réf. -->
          <div
            v-if="selectedRefType === 'sans' && lastCatalogSansRef"
            class="mt-4"
          >
            <div
              class="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center mb-4"
              style="min-height: 100px"
            >
              <div v-if="iframeError" class="p-4 text-center">
                <p class="text-gray-600 mb-2">
                  Impossible d'afficher le document dans l'iframe
                </p>
                <a
                  :href="`${apiUrl}/${lastCatalogSansRef.file_path}`"
                  target="_blank"
                  class="text-[#B70018] hover:underline"
                >
                  Ouvrir le document dans un nouvel onglet
                </a>
              </div>
              <iframe
                v-else
                :src="`${apiUrl}/${lastCatalogSansRef.file_path}`"
                class="w-full max-w-3xl h-[600px] rounded-lg"
                style="border: none; background: #fff"
                @error="handleIframeError"
              />
            </div>
          </div>
          <!-- Avec réf. -->
          <div
            v-else-if="selectedRefType === 'avec' && lastCatalogAvecRef"
            class="mt-4"
          >
            <div
              class="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center mb-4"
              style="min-height: 100px"
            >
              <div v-if="iframeError" class="p-4 text-center">
                <p class="text-gray-600 mb-2">
                  Impossible d'afficher le document dans l'iframe
                </p>
                <a
                  :href="`${apiUrl}/${lastCatalogAvecRef.file_path}`"
                  target="_blank"
                  class="text-[#B70018] hover:underline"
                >
                  Ouvrir le document dans un nouvel onglet
                </a>
              </div>
              <iframe
                v-else
                :src="`${apiUrl}/${lastCatalogAvecRef.file_path}`"
                class="w-full max-w-3xl h-[600px] rounded-lg"
                style="border: none; background: #fff"
                @error="handleIframeError"
              />
            </div>
          </div>
          <div v-else class="mt-4 text-gray-400 text-sm">
            Aucun catalogue disponible
          </div>
        </div>

        <!-- Plan de communication -->
        <div v-if="config" class="bg-white shadow rounded-lg p-6">
          <div
            v-if="currentBrand === 'hv' && config.com_plan_hvname"
            class="bg-gray-100 rounded-lg overflow-hidden mb-6 flex items-center justify-center"
          >
            <iframe
              :src="getDocumentUrl(config.com_plan_hvname)"
              class="w-full max-w-3xl h-[600px] rounded-lg"
              style="border: none"
            />
          </div>
          <div
            v-else-if="currentBrand === 'fendt' && config.com_plan_fendt_name"
            class="bg-gray-100 rounded-lg overflow-hidden mb-6 flex items-center justify-center"
          >
            <iframe
              :src="getDocumentUrl(config.com_plan_fendt_name)"
              class="w-full max-w-3xl h-[600px] rounded-lg"
              style="border: none"
            />
          </div>
          <div
            v-else-if="currentBrand === 'valtra' && config.com_plan_valtra_name"
            class="bg-gray-100 rounded-lg overflow-hidden mb-6 flex items-center justify-center"
          >
            <iframe
              :src="getDocumentUrl(config.com_plan_valtra_name)"
              class="w-full max-w-3xl h-[600px] rounded-lg"
              style="border: none"
            />
          </div>
          <div class="text-center">
            <a
              v-if="currentBrand === 'hv' && config.com_plan_hvname"
              :href="getDocumentUrl(config.com_plan_hvname)"
              target="_blank"
              class="inline-flex items-center justify-center px-4 py-2 bg-[#B70018] text-white rounded-md hover:bg-[#B70018]/90 transition-colors"
            >
              Plan de communication {{ new Date().getFullYear() }}
            </a>
            <a
              v-else-if="currentBrand === 'fendt' && config.com_plan_fendt_name"
              :href="getDocumentUrl(config.com_plan_fendt_name)"
              target="_blank"
              class="inline-flex items-center justify-center px-4 py-2 bg-[#B70018] text-white rounded-md hover:bg-[#B70018]/90 transition-colors"
            >
              Plan de communication {{ new Date().getFullYear() }}
            </a>
            <a
              v-else-if="
                currentBrand === 'valtra' && config.com_plan_valtra_name
              "
              :href="getDocumentUrl(config.com_plan_valtra_name)"
              target="_blank"
              class="inline-flex items-center justify-center px-4 py-2 bg-[#B70018] text-white rounded-md hover:bg-[#B70018]/90 transition-colors"
            >
              Plan de communication {{ new Date().getFullYear() }}
            </a>
          </div>
        </div>
      </section>

      <!-- Footer avec copyright -->
      <footer
        class="text-center text-sm text-gray-500 py-4 border-t border-gray-200"
      >
        &copy; {{ new Date().getFullYear() }} AGCO - Bluecom. Tous droits
        réservés.
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import api from "@/api/api";
import forumService from "@/api/forum/forum.service";
import type { ForumBanner } from "@/types/forum";
import Notification from "@/components/ui/Notification.vue";
import photoCacheService from "@/services/photoCache.service";
import { useConfigStore } from "@/stores/config";
import { usePromoStore } from "@/stores/promo";
import { getAgcoThumbnailUrl, getThumbnailUrl } from "@/utils/image";
import { computed, onMounted, ref, watch } from "@vue/runtime-core";
import { useRouter } from "vue-router";

// Types
interface Catalog {
  file_name: string;
  file_path: string;
}

interface Operation {
  id: number;
  name: string;
  description: string;
  catalog: boolean;
  operationGroup: {
    slug: string;
  };
  order_date_start: string;
  order_date_end: string;
  cover?: string;
}

interface Order {
  reference: string;
}

interface Document {
  id: number;
  name: string;
  thumb_name: string;
}

interface BrandAssets {
  nameBrand: string;
  logoBrand: string;
  coverOperation: string;
}

interface SignatureData {
  id: number;
  url?: string;
  path?: string;
  image_name?: string;
}

interface UserSignature {
  id: number;
  logo: string;
  titre: string;
  info: string;
  contact: string;
  type: "signature" | "address";
}

interface SignatureApiResponse {
  signatures: SignatureData[];
  message?: string;
}

interface LogoApiResponse {
  logo: LogoData;
  message?: string;
}

interface LogoData {
  id: number;
  url?: string;
  path?: string;
  image_name?: string;
}

// Constantes pour le cache
const CACHE_KEYS = {
  CATALOGS: "catalogs_cache",
  DOCUMENTS: "documents_cache",
  AGCO_DOCUMENTS: "agco_documents_cache",
  OPERATION: "operation_cache",
  ORDER: "order_cache",
  BRAND_ASSETS: "brand_assets_cache",
  USER_SIGNATURES: "user_signatures_cache",
  CACHE_TIMESTAMP: "cache_timestamp",
} as const;

// Durée de validité du cache (24 heures en millisecondes)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Fonctions utilitaires pour le cache
const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

const getCache = (key: string): any => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;

  try {
    const { data, timestamp } = JSON.parse(cached);
    if (!isCacheValid(timestamp)) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const setCache = (key: string, data: any): void => {
  const cacheData = {
    data,
    timestamp: Date.now(),
  };
  localStorage.setItem(key, JSON.stringify(cacheData));
};

// Déclaration explicite de l'utilisation des fonctions
const thumbnailUrl = getThumbnailUrl;
const agcoThumbnailUrl = getAgcoThumbnailUrl;

const router = useRouter();
const configStore = useConfigStore();
const promoStore = usePromoStore();
const apiUrl = import.meta.env.VITE_API_URL;

const getDocumentUrl = (filename: string): string =>
  `${apiUrl}/uploads/pdf/com_plans/${filename}`;

const user = computed(() => {
  const userData = localStorage.getItem("user_data");
  return userData ? JSON.parse(userData) : null;
});

// États pour les données
const operation = ref<Operation | null>(null);
const order = ref<Order | null>(null);
const recentDocuments = ref<Document[]>([]);
const documentsAgco = ref<Document[]>([]);
const brandAssets = ref<BrandAssets | null>(null);
const userSignatures = ref<UserSignature[]>([]);
const userLogo = ref<string | null>(null);
const forumBanner = ref<ForumBanner | null>(null);
// Texte du bandeau Forum : la partie avant le « — » est mise en gras (comme les autres bandeaux).
const forumBannerParts = computed(() => {
  const text = forumBanner.value?.text || "Forum AGCO — Inscrivez-vous";
  const idx = text.indexOf("—");
  if (idx === -1) return { bold: text, rest: "" };
  return { bold: text.slice(0, idx).trim(), rest: text.slice(idx).trim() };
});
const config = computed(() => configStore.agcoConfig);

// États pour la gestion du chargement et des erreurs
const isLoading = ref(true);
const showNotification = ref(false);
const notification = ref<{
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
}>({
  type: "error",
  title: "",
  message: "",
});

const currentBrand = computed(() => localStorage.getItem("brand") || "");

// Vérifier si l'utilisateur a le rôle TESTEUR
const isTester = computed(() => {
  const userData = localStorage.getItem("user_data");
  if (!userData) return false;

  try {
    const user = JSON.parse(userData);
    return user.roles && user.roles.includes("ROLE_TESTER");
  } catch (error) {
    console.error("Erreur lors de la lecture des données utilisateur:", error);
    return false;
  }
});

// Vérifier si la date actuelle est dans la période de commande
const isWithinOrderPeriod = computed(() => {
  if (!operation.value?.order_date_start || !operation.value?.order_date_end) {
    return false;
  }

  const now = new Date();
  const startDate = new Date(operation.value.order_date_start);
  const endDate = new Date(operation.value.order_date_end);

  return now >= startDate && now <= endDate;
});

// Déterminer si le bouton "Commander votre catalogue" doit être disponible
const canOrderCatalog = computed(() => {
  // Les testeurs peuvent toujours commander
  if (isTester.value) return true;

  // Les autres utilisateurs peuvent commander uniquement pendant la période
  return isWithinOrderPeriod.value;
});

const brandColors = computed(() => {
  const brand = localStorage.getItem("brand");

  switch (brand) {
    case "fendt":
      return {
        gradient:
          "bg-[#4B7526] after:absolute after:inset-0 after:bg-gradient-to-r after:from-black/20 after:to-transparent",
        text: "text-white",
        textLight: "text-white/90",
        button: "bg-white/20 backdrop-blur-sm hover:bg-white/30",
        skew: "-skew-x-[20deg]",
      };
    case "valtra":
      return {
        gradient:
          "bg-[#B70018] after:absolute after:inset-0 after:bg-gradient-to-r after:from-black/20 after:to-transparent",
        text: "text-white",
        textLight: "text-white/90",
        button: "bg-white/20 backdrop-blur-sm hover:bg-white/30",
        skew: "-skew-x-[20deg]",
      };
    default:
      return {
        gradient:
          "bg-[#B70018] after:absolute after:inset-0 after:bg-gradient-to-r after:from-black/20 after:to-transparent",
        text: "text-white",
        textLight: "text-white/90",
        button: "bg-white/20 backdrop-blur-sm hover:bg-white/30",
        skew: "-skew-x-[20deg]",
      };
  }
});

// Catalogues utilisateur
const userCatalogs = ref<Catalog[]>([]);
const selectedRefType = ref<"sans" | "avec">("sans");

const catalogsSansRef = computed(() =>
  userCatalogs.value.filter((cat: Catalog) => !/ref/i.test(cat.file_name))
);
const catalogsAvecRef = computed(() =>
  userCatalogs.value.filter((cat: Catalog) => /ref/i.test(cat.file_name))
);

const lastCatalogSansRef = computed(() =>
  catalogsSansRef.value.length ? catalogsSansRef.value[catalogsSansRef.value.length - 1] : null
);
const lastCatalogAvecRef = computed(() =>
  catalogsAvecRef.value.length ? catalogsAvecRef.value[catalogsAvecRef.value.length - 1] : null
);

const fetchUserCatalogs = async (): Promise<void> => {
  const uuid = user.value?.uuid;
  const brand = currentBrand.value;
  if (!uuid) return;

  // Clé de cache spécifique à la marque
  const cacheKey = `${CACHE_KEYS.CATALOGS}_${brand || 'all'}`;
  const cachedCatalogs = getCache(cacheKey) as Catalog[];
  if (cachedCatalogs) {
    userCatalogs.value = cachedCatalogs;
    return;
  }

  try {
    const { data } = await api.get(`/operations/catalogs-by-user`, {
      params: { uuid, brand },
    });
    if (data.success) {
      userCatalogs.value = data.catalogs;
      setCache(cacheKey, data.catalogs);
    }
  } catch {
    userCatalogs.value = [];
  }
};

const fetchUserSignatures = async (): Promise<void> => {
  try {
    // Essayer de charger depuis le localStorage d'abord
    const storedSignatures = localStorage.getItem("user_signatures");
    if (storedSignatures) {
      userSignatures.value = JSON.parse(storedSignatures);
    }

    // Vérifier le cache
    const cachedSignatures = getCache(
      CACHE_KEYS.USER_SIGNATURES
    ) as UserSignature[];
    if (cachedSignatures) {
      userSignatures.value = cachedSignatures;
      return;
    }

    // Mettre à jour avec les données du serveur
    const response = await api.get<SignatureApiResponse>("/signature");
    const signatures = response.data.signatures || [];

    // Filtrer les signatures invalides (sans URL ou avec URL vide)
    const validSignatures = signatures.filter(
      (sig) => sig.url && sig.url !== `${apiUrl}/uploads/signatures/`
    );

    userSignatures.value = validSignatures
      .filter((signature) => signature.url) // S'assurer que l'URL existe
      .map((signature) => ({
        id: signature.id,
        logo: signature.url as string, // Forcer le type string car on a filtré les undefined
        titre: signature.image_name || "Signature",
        info: "",
        contact: "",
        type: "signature" as const,
      }));

    // Sauvegarder dans le cache et le localStorage
    setCache(CACHE_KEYS.USER_SIGNATURES, userSignatures.value);
    localStorage.setItem(
      "user_signatures",
      JSON.stringify(userSignatures.value)
    );
  } catch (error) {
    console.warn("Aucune signature trouvée");
    userSignatures.value = [];
  }
};

const fetchUserLogo = async (): Promise<void> => {
  try {
    const response = await api.get<LogoApiResponse>("/logo");
    const logo = response.data.logo;

    if (logo?.url || logo?.path) {
      let url = logo.url || logo.path || "";
      if (url && !url.startsWith("http")) {
        url = import.meta.env.VITE_API_URL + url;
      }
      userLogo.value = url;
      localStorage.setItem("user_logo", JSON.stringify(logo));
    } else {
      userLogo.value = null;
    }
  } catch (error) {
    console.warn("Aucun logo trouvé");
    userLogo.value = null;
  }
};

const fetchData = async (): Promise<void> => {
  isLoading.value = true;
  showNotification.value = false;

  try {
    await configStore.fetchAgcoConfig();
    const brand = localStorage.getItem("brand");

    if (user.value && brand) {
      // Récupération de l'opération
      const cachedOperation = getCache(CACHE_KEYS.OPERATION) as Operation;
      if (cachedOperation) {
        operation.value = cachedOperation;
      } else {
        const { data: operationData } = await api.get(
          `/operations/current?brand=${brand}`
        );
        operation.value = operationData.operation;
        setCache(CACHE_KEYS.OPERATION, operationData.operation);
      }

      // Récupération de la commande
      if (operation.value?.id) {
        const cachedOrder = getCache(CACHE_KEYS.ORDER) as Order;
        if (cachedOrder) {
          order.value = cachedOrder;
        } else {
          try {
            const { data: orderData } = await api.get(
              `/orders/${user.value?.id}`
            );
            order.value = orderData.order;
            setCache(CACHE_KEYS.ORDER, orderData.order);
          } catch (err: unknown) {
            if (
              err &&
              typeof err === "object" &&
              "response" in err &&
              err.response &&
              typeof err.response === "object" &&
              "data" in err.response &&
              err.response.data &&
              typeof err.response.data === "object" &&
              "info" in err.response.data
            ) {
              notification.value = {
                type: "info",
                title: "Information",
                message: err.response.data.info as string,
              };
              showNotification.value = true;
            }
          }
        }
      }

      // Récupération des documents récents
      const cachedDocuments = getCache(CACHE_KEYS.DOCUMENTS) as Document[];
      if (cachedDocuments) {
        recentDocuments.value = cachedDocuments;
      } else {
        const { data: mySupports } = await api.get(
          `/supports/my?brand=${brand}&limit=3`
        );
        recentDocuments.value = mySupports.data || [];
        setCache(CACHE_KEYS.DOCUMENTS, mySupports.data || []);
      }

      // Récupération des documents AGCO
      const cachedAgcoDocuments = getCache(
        CACHE_KEYS.AGCO_DOCUMENTS
      ) as Document[];
      if (cachedAgcoDocuments) {
        documentsAgco.value = cachedAgcoDocuments;
      } else {
        const { data: agcoSupports } = await api.get(
          `/supports/agco?brand=${brand}&limit=3&order=created_at&direction=desc`
        );
        documentsAgco.value = agcoSupports.data || [];
        setCache(CACHE_KEYS.AGCO_DOCUMENTS, agcoSupports.data || []);
      }

      // Récupération des assets de la marque
      const cachedAssets = getCache(CACHE_KEYS.BRAND_ASSETS) as BrandAssets;
      if (cachedAssets) {
        brandAssets.value = cachedAssets;
      } else {
        const { data: assets } = await api.get(`/brands/${brand}/assets`);
        console.log(assets);
        const newBrandAssets: BrandAssets = {
          nameBrand: brand.toUpperCase(),
          logoBrand: assets.logo || "",
          coverOperation: operation.value?.cover || "",
        };
        brandAssets.value = newBrandAssets;
        setCache(CACHE_KEYS.BRAND_ASSETS, newBrandAssets);
      }
    }
  } catch (err: unknown) {
    notification.value = {
      type: "error",
      title: "Erreur de chargement",
      message:
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data
          ? (err.response.data.message as string)
          : "Une erreur est survenue lors du chargement des données",
    };
    showNotification.value = true;
  } finally {
    isLoading.value = false;
  }
};

// Fonction pour forcer le rafraîchissement du cache
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const refreshCache = async (): Promise<void> => {
  try {
    // Sauvegarder les données importantes
    const authToken = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user_data");
    const brand = localStorage.getItem("brand");

    // Vider tout le localStorage
    localStorage.clear();

    // Restaurer les données importantes
    if (authToken) {
      localStorage.setItem("auth_token", authToken);
    }
    if (userData) {
      localStorage.setItem("user_data", userData);
    }
    if (brand) {
      localStorage.setItem("brand", brand);
    }

    // Vider aussi le cache des photos
    photoCacheService.clearAll();

    console.log("Cache vidé avec succès. auth_token et user_data préservés.");

    // Recharger les données
    await fetchData();
    await fetchUserCatalogs();
    await fetchUserSignatures(); // Ajout de la fonction pour charger les signatures
    await fetchUserLogo(); // Ajout de la fonction pour charger le logo

    // Afficher une notification de succès
    notification.value = {
      type: "success",
      title: "Cache vidé",
      message:
        "Le cache a été vidé avec succès. Les données d'authentification ont été préservées.",
    };
    showNotification.value = true;

    // Recharger la page après un court délai
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  } catch (error) {
    console.error("Erreur lors du vidage du cache:", error);
    notification.value = {
      type: "error",
      title: "Erreur",
      message: "Une erreur est survenue lors du vidage du cache.",
    };
    showNotification.value = true;
  }
};

// Modification du onMounted pour inclure la vérification du cache
onMounted(async () => {
  const cacheTimestamp = getCache(CACHE_KEYS.CACHE_TIMESTAMP) as number;
  if (!cacheTimestamp) {
    setCache(CACHE_KEYS.CACHE_TIMESTAMP, Date.now());
  }

  await configStore.fetchAgcoConfig();
  await fetchData();
  await fetchUserCatalogs();
  await fetchUserSignatures(); // Ajout de la fonction pour charger les signatures
  await fetchUserLogo(); // Ajout de la fonction pour charger le logo
  // Charger les bannières promo
  await promoStore.fetchBanners();
  // Charger les réglages du bandeau Forum
  await fetchForumBanner();
});

/** Récupère les réglages du bandeau Forum (visible / image / couleur / texte). */
const fetchForumBanner = async (): Promise<void> => {
  try {
    const config = await forumService.getConfig();
    forumBanner.value = config.banner ?? null;
  } catch (error) {
    console.error("Erreur lors du chargement du bandeau Forum :", error);
    forumBanner.value = null;
  }
};

// Ajout de l'état pour gérer les erreurs d'iframe
const iframeError = ref(false);

// Fonction pour gérer les erreurs d'iframe
const handleIframeError = () => {
  iframeError.value = true;
};

// Réinitialiser l'état d'erreur lors du changement de type
watch(selectedRefType, () => {
  iframeError.value = false;
});
</script>

<script lang="ts">
export default {
  name: "Home",
  components: {
    Notification,
  },
};
</script>

<style>
.aspect-w-16 {
  position: relative;
  padding-bottom: 56.25%;
}

.aspect-w-16 iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

</style>
